import { colors } from "../../constants/colors";
import { FONT_SIZES } from "../../constants/fontSizes";

// Common colors used across components
export const COMMON_COLORS = {
  stepBg: colors.caribbean_current[900],
  headerBg: colors.prussian_blue[500],
  headerColor: colors.white.DEFAULT,
  borderColor: colors.caribbean_current[500],
  accentColor: colors.prussian_blue[500],
};

export const styles = {
  container: {
    display: "flex",
    flexDirection: "column",
    gap: 3,
  },
  title: {
    color: colors.prussian_blue.DEFAULT,
    mb: 4,
    fontSize: FONT_SIZES.TITLE,
  },
  subtitle: {
    color: colors.prussian_blue.DEFAULT,
    fontSize: FONT_SIZES.SUBTITLE,
  },
  paragraph: {
    fontSize: FONT_SIZES.PARAGRAPH,
  },
  workflowDiagram: {
    mt: 4,
    mb: 4,
    display: "flex",
    justifyContent: "center",
  },
  warningContainer: {
    display: "flex",
    alignItems: "flex-start",
    mt: 2,
    color: colors.prussian_blue[700],
  },
  warningIcon: {
    mr: 1,
    mt: 0.5,
  },
  link: {
    textDecoration: "underline",
    color: "inherit",
    "&:hover": {
      color: colors.blue_green.DEFAULT,
    },
    ml: 1,
  },
  listItem: {
    fontSize: FONT_SIZES.PARAGRAPH,
  },
  boldSubtitle: {
    fontWeight: "bold",
    fontSize: FONT_SIZES.PARAGRAPH,
  },
  feedbackIcon: {
    verticalAlign: "middle",
  },

  // WorkflowDiagram styles
  workflowContainer: {
    width: "100%",
    mx: "auto",
    mb: 0,
  },
  workflowTitle: {
    textAlign: "center",
    fontWeight: 700,
    color: COMMON_COLORS.headerBg,
    mb: 7,
    letterSpacing: 0.5,
    fontSize: FONT_SIZES.SUBTITLE,
    width: "100%",
    mx: "auto",
  },
  workflowCardsContainer: {
    display: "flex",
    flexDirection: { xs: "column", md: "row", lg: "row" },
    alignItems: { xs: "stretch", md: "stretch", lg: "stretch" },
    justifyContent: "center",
    gap: { xs: 4, md: 2 },
    width: "100%",
    minWidth: "300px",
    padding: { xs: 0, sm: 0, md: 0, lg: 2 },
  },
  tutorialCard: {
    width: { xs: "100%", sm: "100%", md: "80%", lg: "80%" },
    minWidth: { xs: "100%", sm: "100%", md: 300, lg: 300 },
    bgcolor: COMMON_COLORS.stepBg,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    px: { xs: 2, sm: 3, md: 4, lg: 5 },
    pb: { xs: 2, sm: 3, md: 4, lg: 5 },
    mt: { xs: 1, sm: 2, md: 3, lg: 2 },
    position: "relative",
  },
  tutorialCardNumber: {
    position: "absolute",
    top: -32,
    left: "50%",
    transform: "translateX(-50%)",
    zIndex: 2,
    height: "100%",
  },
  tutorialCardNumberCircle: {
    width: 56,
    height: 56,
    bgcolor: COMMON_COLORS.headerBg,
    color: COMMON_COLORS.headerColor,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 700,
    fontSize: FONT_SIZES.SUBTITLE,
    border: `4px solid ${COMMON_COLORS.accentColor}`,
  },
  tutorialCardHeader: {
    width: "100%",
    height: { xs: "80px", sm: "80px", md: "100px", lg: "100px" },
    bgcolor: COMMON_COLORS.headerBg,
    color: COMMON_COLORS.headerColor,
    borderRadius: 2,
    py: 1.2,
    px: 2,
    mb: 3,
    mt: 4,
    textAlign: "center",
    fontWeight: 700,
    fontSize: FONT_SIZES.SUBTITLE,
    letterSpacing: 0.2,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  tutorialCardContent: {
    width: "100%",
    height: "100%",
    mt: 1,
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    justifyContent: "center",
    overflow: "auto",
  },
  infoField: {
    mb: 1.5,
  },
  infoFieldLabel: {
    fontWeight: 600,
    color: COMMON_COLORS.accentColor,
    mb: 0.5,
    fontSize: FONT_SIZES.PARAGRAPH,
  },
  infoFieldValue: {
    bgcolor: "#fff",
    borderRadius: 1.5,
    px: 1.5,
    py: 0.7,
    fontSize: FONT_SIZES.PARAGRAPH,
    color: COMMON_COLORS.headerBg,
    border: `1px solid ${COMMON_COLORS.borderColor}`,
    fontWeight: 500,
  },
  workflowIcon: {
    mr: 1,
    color: COMMON_COLORS.accentColor,
    fontSize: FONT_SIZES.PARAGRAPH,
  },
  workflowTitleText: {
    fontWeight: 700,
    color: COMMON_COLORS.headerBg,
    fontSize: FONT_SIZES.PARAGRAPH,
  },
  workflowDescription: {
    ml: 4,
    color: "#234",
    fontSize: FONT_SIZES.PARAGRAPH,
  },
  submitButton: {
    bgcolor: "#fff",
    borderRadius: 2,
    px: 2,
    py: 1.2,
    fontWeight: 700,
    color: COMMON_COLORS.headerBg,
    border: `1px solid ${COMMON_COLORS.borderColor}`,
    textAlign: "center",
    fontSize: FONT_SIZES.PARAGRAPH,
    boxShadow: `0 0 0 2px ${COMMON_COLORS.accentColor}33`,
    transition: "box-shadow 0.2s",
  },
  iterateIcon: {
    color: COMMON_COLORS.accentColor,
    fontSize: FONT_SIZES.PARAGRAPH,
    m: 2,
  },
  iterateTitle: {
    color: COMMON_COLORS.accentColor,
    fontWeight: 700,
    mb: 1,
    fontSize: FONT_SIZES.PARAGRAPH,
    textAlign: "center",
  },
  iterateDescription: {
    color: "#234",
    mb: 2,
    textAlign: "center",
    fontSize: FONT_SIZES.PARAGRAPH,
  },

  // TopMenuOverlay styles
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.7)",
    zIndex: "drawer + 1",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  overlayPaper: {
    position: "relative",
    width: "100%",
    maxWidth: "1500px",
    minWidth: { xs: "90%", sm: "800px" },
    maxHeight: "90vh",
    overflow: "auto",
    p: { xs: 2, sm: 4, md: 5 },
    backgroundColor: colors.white.DEFAULT,
  },
  closeButton: {
    position: "absolute",
    right: 8,
    top: 8,
    color: colors.prussian_blue.DEFAULT,
  },
};
